import { useEffect, useEffectEvent, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { ErrorLike } from '@apollo/client';

import { TrainingGroupsDocument } from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { usePagination } from 'common/hooks/usePagination';

interface IUseTrainingGroupListOptions {
    trainingId: string;
}

export const useTrainingGroupList = ({
    trainingId,
}: IUseTrainingGroupListOptions) => {
    const { showApolloError } = useApolloError();
    const [searchValue, setSearchValue] = useState('');

    const { paginationSettings, initializePagination, setPage } =
        usePagination(true);

    const queryVariables = useMemo(
        () => ({
            trainingId,
            offset: paginationSettings.offset,
            first: paginationSettings.first,
            q: searchValue || undefined,
        }),
        [
            trainingId,
            paginationSettings.offset,
            paginationSettings.first,
            searchValue,
        ]
    );

    const { loading, data, error } = useQuery(TrainingGroupsDocument, {
        variables: queryVariables,
    });

    const onError = useEffectEvent((error: ErrorLike) => {
        showApolloError(error);
    });

    useEffect(() => {
        if (error) {
            onError(error);
        }
    }, [error]);

    const connection = data?.paginatedGroups;
    const count = connection?.count;
    const groups = useMemo(
        () =>
            connection?.edges
                ?.map((edge) => edge?.node)
                .filter((node) => !!node) || [],
        [connection?.edges]
    );

    useEffect(() => {
        if (loading || !initializePagination) return;

        initializePagination(count || 0);
    }, [count, loading, initializePagination]);

    return {
        groups,
        groupsCount: count,
        loading,
        paginationSettings,
        searchValue,
        setPage,
        onSearch: setSearchValue,
    };
};
